import {
  TransactionType,
  isTransactionTradingAccountBalanceConversionType,
} from '@utils/transaction.utils'

import { TransactionStatus } from '@model/TransactionStatus'

import { BonusType } from './BonusTransactionDto'
import { TransactionCardDetailsDto } from './CardDetailsDto'
import { NameDto } from './NameDto'
import type { PaymentProviderType } from './PaymentProviderType'
import { PlatformTypeEnum } from './PlatformTypeEnum'
import { TransactionLinksDto } from './TransactionLinksDto'
import { CurrencyType, WalletDto } from './WalletDto'

export interface TradingAccountTransactionDto {
  id: string
  directionType: NameDto<TransactionDirection>
  amount: number
  tradingAccount: {
    id: string
    name: string
    tickmillProduct: {
      id: 1
      name: 'FX & CFDs'
    }
    isMultiCurrency: false
    platformType: NameDto<PlatformTypeEnum>
    platformTypeTooltip: {
      childPlatformType: { id: number; name: string }
      infoCard: {
        link: {
          text: string
          to: string
        }
        text: string
        title: string
      }
      parentPlatformType: { id: number; name: string }
    } | null
  }
  wallet: NameDto
  client: NameDto
  tickmillCompany: NameDto
  currency: NameDto<CurrencyType>
  currencyRate: 1.0
  otherAmount: 11.0
  otherCurrency: NameDto<CurrencyType>
}

export interface TransactionDto {
  id: string
  amount: number
  referenceId: string
  type: NameDto<TransactionType>
  state: NameDto<TransactionStatus>
  groupReference?: NameDto<string>
  linkedFrom: TransactionLinksDto[]
  linkedTo: TransactionLinksDto[]
  comment: string | null
  createdDate: string
  updatedDate: string
  reverseTransaction?: NameDto<string>
  reversedTransactions?: {
    id: string
    name: string
    type: NameDto
    state: NameDto
  }[]
  wallet?: NameDto<string>
  walletTransactions: WalletTransactionDto[]
  tradingAccountTransactions: TradingAccountTransactionDto[]
  cardDetails?: TransactionCardDetailsDto | null
}

export const isTransactionTradingAccountBalanceConversion = (transaction: TransactionDto) => {
  return isTransactionTradingAccountBalanceConversionType(transaction?.type.id)
}

export interface WalletTransactionDto {
  id: string
  directionType: NameDto<TransactionDirection>
  otherCurrency: NameDto<CurrencyType>
  amount: number
  bonusTransaction: {
    bonusReserved: number
    bonusTransactionStatus: { id: number; name: string }
    bonusType: BonusType
    expiryDate: string
    id: string
    lotsRequired: number
    lotsTraded: number
    tradedProfit: number
  }
  wallet: WalletDto
  currencyRate: number
  otherAmount: number
  paymentProviderTransaction: null | PaymentProviderDetails
  paymentAgentTransaction?: {
    account: string
    documentId: string
    id: string
    paymentAgent: { id: string; name: string }
    system: string
  }
  ibCommisionTransaction?: {
    introducingBroker: NameDto<string>
  }
}

interface PaymentProviderDetails {
  id: string
  providerReference: null | any
  paymentProvider: {
    id: string
    name: string
    externalName: string
    category: NameDto<PaymentProviderType>
    logoUrl?: string | null
  }
  isConnectedToService: boolean
  currency: NameDto<string>
  request: {
    [key: string]: string | null | undefined
    bankCode?: string | null
    bankName?: string | null
    externalAccount?: string | null
    user_ip: string
    swift: string
    bank_name: string
    user_zip: string
    method_id: string
    reference: string
    user_city: string
    company_id: string
    user_email: string
    user_phone: string
    language_id: string
    user_gender: string
    user_address: string
    wallet_amount: string
    payment_amount: string
    user_birthdate: string
    user_last_name: string
    wallet_type_id: string
    user_country_id: string
    user_first_name: string
    external_account: null
    wallet_currency_id: CurrencyType
    payment_currency_id: CurrencyType
  } | null
  response: {
    update: {
      externalAccount?: string | null
      providerReference?: string | null
      providerResponse?: string | null
      paymentCurrencyId?: string | null
      details: {
        bankAddress: string | null
        bankBranch: string | null
        bankCode: string | null
        bankName: string | null
        billingCityCode: string | null
        billingCityName: string | null
        billingStateCode: string | null
        billingStateName: string | null
        cardMonth: string | null
        cardYear: string | null
        nameOfAccount: string | null
      } | null
    }
  } | null
}

export interface TransactionHistoryItem {
  id: string
  reason: string | null
  createdDate: string
  transactionState: NameDto<TransactionStatus>
  transactionStateReason: NameDto
}

export interface TransactionStateHistoryDto {
  translation: NameDto<string>
  history: TransactionHistoryItem[]
}

export enum TransferTypeEnum {
  WALLET_TO_WALLET = 1,
  WALLET_TO_TA = 2,
  TA_TO_WALLET = 3,
  TA_TO_TA = 4,
}

export enum TransactionDirection {
  Debit = 100,
  Credit = 200,
}
